// Dossier magazine — DEMI-TOUR À MOTO
// Contenu original signé Bruno · Équipe Code 2 Roues · CODE 2 ROUES · 2026
import {
  Eye, Hand, AlertTriangle, Sparkles, Target, Gauge, Compass,
  ShieldCheck, TrendingUp, Route, HelpCircle, Repeat, Lightbulb,
  RotateCcw, Footprints, Zap,
} from 'lucide-react';

export const demiTourMotoArticle = {
  slug: 'demi-tour-moto-methode',
  weekVolume: 33,
  weekYear: 2026,
  monthLabel: 'Août 2026',
  category: 'Technique & maîtrise',
  shortTitle: 'Demi-tour à moto — Tourner court sans tomber',
  title: "Demi-tour à moto : la méthode pour tourner court sans perdre l'équilibre",
  kicker: '🔄 Dossier technique · Signé Bruno · Équipe Code 2 Roues',
  subtitle:
    "Faire demi-tour paraît simple. En réalité, cette manœuvre concentre <strong>toutes les difficultés de la basse vitesse</strong> : rouler lentement, tourner fortement, gérer l'équilibre, regarder derrière soi… et conserver suffisamment de motricité. Chez CODE 2 ROUES, on décompose.",
  heroImage: '/images/articles/demi-tour-plateau-cercle.png',
  heroImageAlt: 'Motard vu de dos exécutant un demi-tour sur un cercle tracé au sol du plateau, cônes de balisage — lumière de fin de journée',
  coverPoster: '/images/articles/poster-demi-tour-horizontal.png',
  coverPosterAlt: 'Couverture éditoriale CODE 2 ROUES — Demi-tour à moto : 5 étapes pour réaliser un demi-tour sécurisé (observation, signalisation, allure lente, braquage, stabilité)',
  posterHorizontal: '/images/articles/poster-demi-tour-horizontal.png',
  posterVertical: '/images/articles/poster-demi-tour-vertical.png',
  posterVerticalAlt: 'Poster vertical CODE 2 ROUES — Demi-tour à moto : 5 étapes pour réaliser un demi-tour sécurisé (observation, signalisation, allure lente, braquage, stabilité)',
  topRightKicker: { line1: 'Technique & maîtrise', line2: 'Basse vitesse · Plateau' },
  readTimeMin: 10,
  publishedAt: '2026-08-16',
  seoDescription:
    "Demi-tour à moto : méthode pédagogique complète. Regard, point de patinage, frein arrière, contrepoids et 4 exercices progressifs (cercle large, cercle serré, huit, demi-tour). Guide A1/A2 par un moniteur ECSR.",
  seoKeywords:
    'demi-tour moto, moto basse vitesse, point de patinage moto, contrepoids moto, plateau A2, maniabilité moto, exercice 8 moto, frein arrière moto, demi-tour plateau permis',
  about: 'Dossier pédagogique — maîtriser le demi-tour à moto pour A1/A2 et conduite quotidienne.',
  chapo: {
    kicker: "L'idée à retenir",
    body: "Un demi-tour réussi n'est <strong>pas</strong> une question de force. C'est une question de <strong>regard, d'équilibre, de motricité et de position</strong>. Lorsque la moto commence à pencher, le réflexe du débutant (couper les gaz, débrayer, sortir le pied) est précisément celui qui la fait tomber.",
    sideStat: {
      kicker: "Les 6 clés du demi-tour",
      big: 'REGARD\nÉQUILIBRE\nEMBRAYAGE\nMOTRICITÉ\nALLURE\nPOSITION',
      body: 'Six actions coordonnées — jamais isolées. La force n\'en fait pas partie.',
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "On ne regarde pas ce que l'on veut éviter. On regarde là où l'on veut sortir. La moto va toujours là où va le regard.",
    source: 'Bruno · Équipe Code 2 Roues — Enseignant ECSR, CODE 2 ROUES',
  },
  sections: [
    {
      part: 'Partie 1 — Le contexte',
      title: "Pourquoi le demi-tour compte (bien au-delà du plateau)",
      intro: "Le demi-tour n'est pas qu'un exercice d'examen. Dans la vraie vie, il est partout — et un motard incapable de tourner à basse vitesse multiplie les manœuvres… donc les occasions de tomber.",
      cards: [
        { icon: Route, accent: '#0ea5e9', kicker: 'Ville', title: 'Rue, parking, station', body: "Chaque déplacement urbain peut imposer un demi-tour dans un espace réduit." },
        { icon: Compass, accent: '#16a34a', kicker: 'Route', title: 'Après une erreur de direction', body: "Rater une sortie ou une intersection : demi-tour propre = zéro stress." },
        { icon: ShieldCheck, accent: '#cf2031', kicker: 'Sécurité', title: 'Moins de manœuvres = moins de risques', body: "Un demi-tour maîtrisé évite trois marches arrière hasardeuses." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: Lightbulb,
          accent: '#0ea5e9',
          body: "<strong>Maîtriser le demi-tour</strong>, c'est maîtriser la moto <em>lorsque la vitesse ne vient plus vous aider</em>. C'est là que se joue la différence entre un motard à l'aise… et un motard sur les nerfs.",
        },
      ],
    },
    {
      part: 'Partie 2 — La physique',
      title: "Pourquoi la basse vitesse est-elle si piégeuse ?",
      intro: "À allure suffisante, la moto possède une <strong>stabilité dynamique</strong> importante (effet gyroscopique). À très basse vitesse, cette stabilité s'effondre — et c'est au conducteur de compenser.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: 'ALLURE NORMALE',
          title: 'La moto se stabilise seule',
          intro: "À partir d'une certaine vitesse, la moto tient debout d'elle-même :",
          points: [
            { ok: true, text: 'Effet gyroscopique des roues' },
            { ok: true, text: 'Direction auto-centrée' },
            { ok: true, text: 'Peu d\'action nécessaire du conducteur' },
          ],
        },
        {
          accent: '#f97316',
          kicker: 'BASSE VITESSE',
          title: 'Le conducteur devient le stabilisateur',
          intro: "Il faut alors gérer <strong>simultanément</strong> :",
          points: [
            { ok: false, text: 'Direction (guidon fortement braqué)' },
            { ok: false, text: 'Inclinaison + appuis' },
            { ok: false, text: 'Motricité (gaz + embrayage + frein arrière)' },
            { ok: false, text: 'Regard tourné vers la sortie' },
          ],
        },
      ],
    },
    {
      part: 'Partie 3 — Le regard',
      title: 'La première commande n\'est pas le guidon',
      intro: "Avant l'embrayage, avant le guidon, avant les gaz — il y a <strong>le regard</strong>. Votre moto va là où vont vos yeux. Toujours.",
      cards: [
        { icon: Eye, accent: '#cf2031', kicker: 'ERREUR N°1', title: 'Regarder le sol', body: "Cône, ligne blanche, trottoir, endroit qu'on ne veut pas toucher… Le cerveau organise l'action AUTOUR de cette zone." },
        { icon: RotateCcw, accent: '#16a34a', kicker: 'BONNE PRATIQUE', title: 'Tourner la tête, pas juste les yeux', body: "Cherchez la SORTIE du demi-tour. Presque comme si vous vouliez regarder derrière votre épaule." },
        { icon: Target, accent: '#0ea5e9', kicker: 'MESSAGE CLAIR', title: '« C\'est là que je veux aller »', body: "Le cerveau reçoit une information sans ambiguïté et coordonne naturellement les commandes." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>La règle absolue :</strong> on ne regarde pas ce qu'on veut éviter, on regarde là où on veut sortir. Un motard dont les mains disent GAUCHE mais dont les yeux disent TOUT DROIT… élargit systématiquement.",
        },
      ],
    },
    {
      part: 'Partie 4 — La motricité',
      title: 'Le trio gagnant : gaz + embrayage + frein arrière',
      intro: "À basse vitesse, la motricité est <strong>votre alliée</strong>. Une moto qui tourne sans énergie tombe. Le secret : la doser finement grâce au trio commande.",
      cards: [
        { icon: Zap, accent: '#f59e0b', kicker: '✊ GAZ', title: 'Régime moteur régulier', body: "On ne coupe PAS les gaz quand ça penche. Un régime stable = une source d'énergie constante." },
        { icon: Hand, accent: '#7c3aed', kicker: '✋ EMBRAYAGE', title: 'Point de patinage', body: "L'embrayage n'est pas un interrupteur. C'est un VARIATEUR de motricité. On dose entre embrayé et débrayé." },
        { icon: Footprints, accent: '#0ea5e9', kicker: '🦶 FREIN ARRIÈRE', title: 'Régulateur d\'allure', body: "Le frein arrière stabilise et régule finement la vitesse — sans casser l'équilibre comme le ferait le frein avant." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: Lightbulb,
          accent: '#16a34a',
          body: "<strong>Moto lente mais encore motrice</strong> — c'est très différent d'une moto qui avance sur son élan en perdant son énergie. Le point de patinage transforme votre moto en scooter à variateur, avec une précision millimétrique.",
        },
      ],
    },
    {
      part: 'Partie 5 — Le piège',
      title: 'Frein avant à basse vitesse : à manipuler avec soin',
      intro: "À très basse vitesse, guidon braqué, une action brutale sur le frein avant peut provoquer un déséquilibre rapide. La moto est déjà lente, orientée, inclinée — une forte décélération avant perturbe brutalement son équilibre.",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: 'À ÉVITER',
          title: "Frein avant brutal en plein demi-tour",
          intro: "Combinaison redoutable :",
          points: [
            { ok: false, text: 'Guidon fortement braqué' },
            { ok: false, text: 'Moto inclinée à basse vitesse' },
            { ok: false, text: 'Serrage brusque du levier avant' },
            { ok: false, text: '= Chute quasi assurée' },
          ],
        },
        {
          accent: '#16a34a',
          kicker: 'BONNE PRATIQUE',
          title: 'Frein arrière pour réguler',
          intro: "Pour ajuster l'allure pendant la manœuvre :",
          points: [
            { ok: true, text: 'Pied droit dose progressivement' },
            { ok: true, text: 'La moto reste stable et motrice' },
            { ok: true, text: 'Peut être combiné avec le point de patinage' },
            { ok: true, text: 'Frein avant = seulement en toute fin, à l\'arrêt' },
          ],
        },
      ],
      callouts: [
        {
          icon: HelpCircle,
          accent: '#eab308',
          body: "Ce n'est <em>pas</em> une interdiction absolue de toucher au frein avant à basse vitesse. C'est une invitation à <strong>comprendre son action</strong> et à rester progressif. Le frein arrière reste beaucoup plus confortable pour le demi-tour.",
        },
      ],
    },
    {
      part: 'Partie 6 — Le corps',
      title: 'Contrepoids : quand la moto penche mais pas vous',
      intro: "À très basse vitesse, contrairement au virage routier classique, on peut <strong>dissocier</strong> moto et conducteur. La moto s'incline vers l'intérieur, le corps reste vers l'extérieur. C'est le contrepoids.",
      cards: [
        { icon: Repeat, accent: '#7c3aed', kicker: '🏍️ MOTO', title: "S'incline vers l'intérieur", body: "Elle accepte son inclinaison naturelle. Vous ne luttez PAS contre." },
        { icon: TrendingUp, accent: '#0ea5e9', kicker: '🧍 BUSTE', title: "Reste vers l'extérieur", body: "Vous conservez votre buste plus vertical, comme un contrepoids qui empêche de « tomber avec » la moto." },
        { icon: Compass, accent: '#16a34a', kicker: '👀 REGARD', title: 'Toujours vers la sortie', body: "Le regard suit la trajectoire — jamais le sol, jamais l'obstacle qu'on veut éviter." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#f59e0b',
          body: "<strong>Le contrepoids n'est pas une contorsion.</strong> Pas question de se jeter exagérément à l'extérieur. Le mouvement doit rester <em>souple, progressif et adapté</em> à la situation. On accompagne — on ne force pas.",
        },
      ],
    },
    {
      part: 'Partie 7 — La pratique',
      title: '4 exercices progressifs — la progression CODE 2 ROUES',
      intro: "Progression pédagogique. Chaque exercice construit le suivant — <strong>ne saute pas d'étape</strong>. Le but n'est jamais le chrono ou le rayon minimum. C'est la maîtrise.",
      orderedList: [
        "<strong>Exercice 1 — Le grand cercle.</strong> Diamètre confortable. Travaillez uniquement : regard tourné, point de patinage, régularité moteur, frein arrière si besoin, décontraction des bras. Objectif : plusieurs tours sans poser le pied. Pas de chrono, pas de recherche de serrage.",
        "<strong>Exercice 2 — Réduire progressivement le cercle.</strong> Quand le grand cercle est maîtrisé, réduisez le diamètre de 20 cm. Puis encore 20 cm. Cette progression révèle naturellement plus de braquage, plus de précision, meilleure utilisation du regard, éventuellement du contrepoids. On réduit UNIQUEMENT quand la maîtrise augmente.",
        "<strong>Exercice 3 — Le huit.</strong> Reliez un cercle gauche et un cercle droit. Le point-clé est le <em>croisement</em> : la moto se redresse, PUIS le regard bascule vers le prochain virage. Cet exercice force à changer de direction, de regard, d'équilibre et de braquage en continu.",
        "<strong>Exercice 4 — Le demi-tour.</strong> Situation réelle. Entrée en ligne droite → je ralentis → je tourne la tête → je conserve ma motricité → je dose au point de patinage → je régule au frein arrière → je laisse la moto tourner (sans lutter) → je regarde la sortie et j'accompagne son redressement.",
      ],
      callouts: [
        {
          icon: Target,
          accent: '#cf2031',
          body: "<strong>Ne cherche pas immédiatement le demi-tour le plus serré possible.</strong> Cherche d'abord un demi-tour PROPRE. Puis un demi-tour RÉGULIER. Puis un demi-tour PLUS LENT. Et seulement ensuite, un demi-tour PLUS SERRÉ. La maîtrise se construit par étapes.",
        },
      ],
    },
    {
      part: 'Partie 8 — Les erreurs',
      title: 'Les 7 erreurs classiques à identifier chez soi',
      intro: "Corriger un demi-tour raté, ce n'est pas répéter « tourne plus ! ». C'est identifier la <strong>vraie cause</strong> derrière le pied posé ou l'élargissement.",
      cards: [
        { icon: Eye, accent: '#cf2031', kicker: '❌ 1', title: 'Regarder le sol', body: "Le cerveau organise l'action autour de la zone regardée. Résultat : chute vers ce qu'on fixait." },
        { icon: AlertTriangle, accent: '#cf2031', kicker: '❌ 2', title: 'Fixer l\'obstacle', body: "Cône, ligne, trottoir : le regard s'aimante. La moto suit." },
        { icon: Zap, accent: '#cf2031', kicker: '❌ 3', title: 'Couper complètement les gaz', body: "Plus de motricité = plus d'équilibre. La moto s'effondre." },
        { icon: Hand, accent: '#cf2031', kicker: '❌ 4', title: 'Débrayer et laisser mourir l\'allure', body: "L'embrayage utilisé en interrupteur au lieu de variateur." },
        { icon: Sparkles, accent: '#cf2031', kicker: '❌ 5', title: 'Se crisper sur le guidon', body: "Bras verrouillés = guidon qui ne peut plus travailler. Instabilité amplifiée." },
        { icon: Repeat, accent: '#cf2031', kicker: '❌ 6', title: 'Refuser toute inclinaison', body: "Une moto qui tourne DOIT accepter de pencher. La maintenir droite empêche le braquage." },
        { icon: Gauge, accent: '#cf2031', kicker: '❌ 7', title: 'Vouloir aller trop lent trop tôt', body: "Avant de chercher PLUS LENT, cherche PLUS STABLE. La vitesse minimale se conquiert progressivement." },
        { icon: Footprints, accent: '#f59e0b', kicker: '🦶 Le pied posé', title: 'Cherche la cause, pas la conséquence', body: "Baisser le regard ? Couper les gaz ? Débrayer ? Freiner brutalement ? Verrouiller les bras ? Hésiter sur la direction ? La cause est TOUJOURS ailleurs." },
      ],
      cardsCols: 4,
    },
    {
      part: 'Cas particuliers',
      title: 'Petit gabarit, pente, dévers, gravier — que change-t-on ?',
      intro: "Le demi-tour de base est le même pour tous. Mais certaines situations exigent des ajustements — voire… de renoncer à faire le demi-tour en une seule fois.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: '👤 Petit gabarit',
          title: 'La technique remplace la taille',
          intro: "Pendant un demi-tour réussi, les pieds n'ont pas vocation à être au sol :",
          points: [
            { ok: true, text: 'Maîtrise du point de patinage' },
            { ok: true, text: 'Régularité de l\'allure' },
            { ok: true, text: 'Regard bien tourné' },
            { ok: true, text: 'Confiance dans la moto' },
          ],
        },
        {
          accent: '#f97316',
          kicker: '⛰️ Pente / dévers / gravier',
          title: 'Observer avant de s\'engager',
          intro: "Avant la manœuvre, on regarde : pente, dévers, adhérence, espace, circulation. Puis on décide :",
          points: [
            { ok: true, text: 'Demi-tour possible en une fois ? OK' },
            { ok: false, text: 'Manœuvre douteuse : plusieurs temps' },
            { ok: true, text: 'Marge et douceur augmentées' },
            { ok: true, text: 'Un bon motard choisit la solution SÛRE, pas la plus démonstrative' },
          ],
        },
      ],
    },
    {
      part: 'À retenir',
      title: 'La méthode CODE 2 ROUES — 6 réflexes coordonnés',
      cards: [
        { icon: Eye, accent: '#0ea5e9', kicker: '👀', title: 'REGARD', body: 'Je cherche ma sortie très tôt. Tête tournée, pas juste les yeux.' },
        { icon: Hand, accent: '#7c3aed', kicker: '👐', title: 'DÉCONTRACTION', body: 'Bras souples. Je laisse le guidon travailler.' },
        { icon: Zap, accent: '#f59e0b', kicker: '✊', title: 'GAZ', body: 'Régime régulier. Je NE coupe PAS quand ça penche.' },
        { icon: Hand, accent: '#cf2031', kicker: '✋', title: 'EMBRAYAGE', body: 'Point de patinage. Je dose entre embrayé et débrayé.' },
        { icon: Footprints, accent: '#0ea5e9', kicker: '🦶', title: 'FREIN ARRIÈRE', body: 'Je régule finement l\'allure sans casser l\'équilibre.' },
        { icon: Repeat, accent: '#16a34a', kicker: '⚖️', title: 'ÉQUILIBRE', body: 'J\'accepte que la moto s\'incline. Je ne lutte pas.' },
        { icon: TrendingUp, accent: '#7c3aed', kicker: '🧍', title: 'POSITION', body: 'Contrepoids : moto vers l\'intérieur, buste vers l\'extérieur.' },
        { icon: Target, accent: '#cf2031', kicker: '🎯', title: 'SORTIE', body: 'Je regarde où je veux aller. La moto suivra.' },
      ],
      cardsCols: 4,
    },
  ],
  ctaFinal: {
    kicker: 'Pratique guidée',
    title: 'Enchaîne avec le livret plateau interactif',
    body: "Le <strong>demi-tour à allure réduite</strong> fait partie des <strong>6 manœuvres officielles</strong> du plateau A2 (M2). Retrouve-le détaillé étape par étape dans le livret, avec cases à cocher et PDF imprimable.",
    primary: { label: 'Ouvrir le livret Plateau', to: '/livret-plateau' },
    secondary: { label: 'Voir la fiche plateau complète', to: '/epreuves/plateau' },
  },
};
